/**
 * @file     preApprovalFlow.ts
 * @purpose  Three-answer affordability estimate (income, monthly debts, down
 *           payment) computed by the core lender-ceiling engine and shown free,
 *           plus the opt-in broker referral capture opened from the result.
 */

import {
  lenderCeiling,
  TYPICAL_CONTRACT_RATE,
  type LenderCeiling,
} from '@/core/finance/lenderCeiling';
import { AMORTIZATION_MONTHS, amortizedPayment } from '@/core/finance/mortgage';
import type { FlowConfig } from '../types';

/** Shared with the inline picker on the hub and city-page heroes. */
export const INCOME_RANGE_OPTIONS = [
  { value: 'under-60k', labelKey: 'preApprovalFlow.income.under60k', labelDefault: 'Under $60k' },
  { value: '60-90k', labelKey: 'preApprovalFlow.income.60to90k', labelDefault: '$60k to $90k' },
  { value: '90-120k', labelKey: 'preApprovalFlow.income.90to120k', labelDefault: '$90k to $120k' },
  { value: '120-160k', labelKey: 'preApprovalFlow.income.120to160k', labelDefault: '$120k to $160k' },
  { value: '160-200k', labelKey: 'preApprovalFlow.income.160to200k', labelDefault: '$160k to $200k' },
  { value: '200-250k', labelKey: 'preApprovalFlow.income.200to250k', labelDefault: '$200k to $250k' },
  { value: '250k-plus', labelKey: 'preApprovalFlow.income.250kPlus', labelDefault: 'Over $250k' },
];

export const MONTHLY_DEBT_OPTIONS = [
  { value: 'none', labelKey: 'preApprovalFlow.debt.none', labelDefault: 'None' },
  { value: 'under-500', labelKey: 'preApprovalFlow.debt.under500', labelDefault: 'Under $500' },
  { value: '500-1000', labelKey: 'preApprovalFlow.debt.500to1000', labelDefault: '$500 to $1,000' },
  { value: '1000-2000', labelKey: 'preApprovalFlow.debt.1000to2000', labelDefault: '$1,000 to $2,000' },
  { value: '2000-plus', labelKey: 'preApprovalFlow.debt.2000Plus', labelDefault: 'Over $2,000' },
];

export const DOWN_PAYMENT_OPTIONS = [
  { value: 'under-25k', labelKey: 'preApprovalFlow.downPaymentAmount.under25k', labelDefault: 'Under $25k' },
  { value: '25-50k', labelKey: 'preApprovalFlow.downPaymentAmount.25to50k', labelDefault: '$25k to $50k' },
  { value: '50-100k', labelKey: 'preApprovalFlow.downPaymentAmount.50to100k', labelDefault: '$50k to $100k' },
  { value: '100-200k', labelKey: 'preApprovalFlow.downPaymentAmount.100to200k', labelDefault: '$100k to $200k' },
  { value: '200k-plus', labelKey: 'preApprovalFlow.downPaymentAmount.200kPlus', labelDefault: 'Over $200k' },
];

/**
 * One representative figure per answer band. The band keys are the stored
 * answer values (FUB note labels and saved profiles read them), so they never
 * change; only this mapping does. Open-ended bands take a point just inside
 * the stated edge rather than an optimistic midpoint.
 */
const INCOME_POINT: Record<string, number> = {
  'under-60k': 50_000,
  '60-90k': 75_000,
  '90-120k': 105_000,
  '120-160k': 140_000,
  '160-200k': 180_000,
  '200-250k': 225_000,
  '250k-plus': 275_000,
  // Legacy band from the first version of the flow.
  'over-160k': 180_000,
};

const MONTHLY_DEBT_POINT: Record<string, number> = {
  none: 0,
  'under-500': 250,
  '500-1000': 750,
  '1000-2000': 1_500,
  '2000-plus': 2_500,
};

const DOWN_PAYMENT_POINT: Record<string, number> = {
  'under-25k': 20_000,
  '25-50k': 37_500,
  '50-100k': 75_000,
  '100-200k': 150_000,
  '200k-plus': 250_000,
};

export type PreApprovalAffordability = {
  /** Purchase price the answers could carry, rounded to the nearest $5,000. */
  price: number;
  /** Monthly principal and interest on that mortgage at the typical contract rate. */
  monthlyPayment: number;
  annualIncome: number;
  monthlyDebt: number;
  downPayment: number;
  ceiling: LenderCeiling;
};

const roundTo = (value: number, step: number) => Math.round(value / step) * step;

function bandFor(value: number, edges: readonly (readonly [number, string])[], top: string): string {
  for (const [upTo, band] of edges) if (value < upTo) return band;
  return top;
}

/** The income answer for a yearly figure another calculator already has. */
export function incomeRangeFor(annualIncome: number): string {
  return bandFor(annualIncome, [[60_000, 'under-60k'], [90_000, '60-90k'], [120_000, '90-120k'], [160_000, '120-160k'], [200_000, '160-200k'], [250_000, '200-250k']], '250k-plus');
}

/** The debts answer for a monthly figure. */
export function monthlyDebtRangeFor(monthlyDebt: number): string {
  if (monthlyDebt <= 0) return 'none';
  return bandFor(monthlyDebt, [[500, 'under-500'], [1_000, '500-1000'], [2_000, '1000-2000']], '2000-plus');
}

/** The down payment answer for a savings figure. */
export function downPaymentRangeFor(savings: number): string {
  return bandFor(savings, [[25_000, 'under-25k'], [50_000, '25-50k'], [100_000, '50-100k'], [200_000, '100-200k']], '200k-plus');
}

/**
 * The affordability estimate for three answers, from the same lender-ceiling
 * engine the property finder uses. Null until all three are answered. This
 * is an estimate of a price, never a pre-approval or an amount a lender has
 * agreed to lend.
 */
export function preApprovalAffordability(answers: Record<string, string>): PreApprovalAffordability | null {
  const annualIncome = INCOME_POINT[answers.incomeRange ?? ''];
  const monthlyDebt = MONTHLY_DEBT_POINT[answers.monthlyDebtRange ?? ''];
  const downPayment = DOWN_PAYMENT_POINT[answers.downPaymentRange ?? ''];
  if (annualIncome == null || monthlyDebt == null || downPayment == null) return null;
  const ceiling = lenderCeiling({ monthlyGrossIncome: annualIncome / 12, downPayment, monthlyDebt });
  return {
    price: roundTo(ceiling.purchasePrice, 5_000),
    monthlyPayment: roundTo(amortizedPayment(ceiling.mortgage, TYPICAL_CONTRACT_RATE, AMORTIZATION_MONTHS), 10),
    annualIncome,
    monthlyDebt,
    downPayment,
    ceiling,
  };
}

const SOURCE_PAGE = '/pre-approval-check';
/** Entry pages allowed to claim attribution via ?source=. */
const SOURCE_RE = /^\/[a-z0-9/-]{0,80}$/;

/**
 * The lead carries where the check was entered from (the hub, a city page, an
 * ad landing), not the tool's own path.
 */
export function preApprovalSourcePage(source: string | null | undefined): string {
  return source && SOURCE_RE.test(source) ? source : SOURCE_PAGE;
}

export const PRE_APPROVAL_FLOW_CONFIG: FlowConfig = {
  flowId: 'pre_approval_readiness',
  slug: '/pre-approval-check',
  leadType: 'pre-approval',
  // v1 drafts contain removed readiness questions and must not resume here.
  draftVersion: 2,
  steps: [
    {
      id: 'income',
      type: 'pills',
      headlineKey: 'preApprovalFlow.income.title',
      headlineDefault: 'What is your household income before tax?',
      subtextKey: 'preApprovalFlow.income.description',
      subtextDefault: 'For everyone on the mortgage.',
      ctaKey: 'preApprovalFlow.actions.continue',
      ctaDefault: 'Continue',
      pillGroups: [
        {
          answerKey: 'incomeRange',
          labelKey: 'preApprovalFlow.income.label',
          labelDefault: 'Household income',
          required: true,
          options: INCOME_RANGE_OPTIONS,
        },
      ],
    },
    {
      id: 'debt',
      type: 'pills',
      headlineKey: 'preApprovalFlow.debt.title',
      headlineDefault: 'How much do you pay toward debts each month?',
      subtextKey: 'preApprovalFlow.debt.description',
      subtextDefault: 'Car loans, student loans, credit cards, lines of credit and support payments.',
      ctaKey: 'preApprovalFlow.actions.continue',
      ctaDefault: 'Continue',
      pillGroups: [
        {
          answerKey: 'monthlyDebtRange',
          labelKey: 'preApprovalFlow.debt.label',
          labelDefault: 'Monthly debts',
          required: true,
          options: MONTHLY_DEBT_OPTIONS,
        },
      ],
    },
    {
      id: 'downpayment',
      type: 'pills',
      headlineKey: 'preApprovalFlow.downPaymentAmount.title',
      headlineDefault: 'How much do you have for a down payment?',
      subtextKey: 'preApprovalFlow.downPaymentAmount.description',
      subtextDefault: 'Savings plus any confirmed family gift. Not closing costs.',
      ctaKey: 'preApprovalFlow.actions.continue',
      ctaDefault: 'Continue',
      pillGroups: [
        {
          answerKey: 'downPaymentRange',
          labelKey: 'preApprovalFlow.downPaymentAmount.label',
          labelDefault: 'Down payment saved',
          required: true,
          options: DOWN_PAYMENT_OPTIONS,
        },
      ],
    },
    {
      id: 'result',
      type: 'readinessResult',
      headlineKey: 'preApprovalFlow.result.title',
      headlineDefault: 'Your affordability estimate',
      terminal: true,
    },
    // Not on the step path (the result is terminal). The result opens this
    // capture in a sheet when the visitor asks for a broker introduction.
    {
      id: 'broker',
      type: 'capture',
      headlineKey: 'preApprovalFlow.broker.title',
      headlineDefault: 'Get introduced to mortgage brokers',
      subtextKey: 'preApprovalFlow.broker.description',
      subtextDefault:
        'Learn how to get a pre-approval from independent mortgage brokers. Your answers and estimate go with the introduction.',
      ctaKey: 'preApprovalFlow.broker.submit',
      ctaDefault: 'Get introduced',
      captureFields: ['firstName', 'lastName', 'email', 'phone'],
      requiredCaptureFields: ['firstName', 'lastName', 'email', 'phone'],
      advanceOnPendingVerification: true,
      disclosureKey: 'preApprovalFlow.broker.disclosure',
      disclosureDefault:
        'Referral fee: if you work with a broker Ray introduces and a mortgage closes, that broker may pay Ray Rasouli a referral fee. The broker pays it, it adds no cost to you, and you are under no obligation to use any broker.',
      disclosureLinkHref: '/disclosure',
      disclosureLinkLabelKey: 'preApprovalFlow.broker.disclosureLink',
      disclosureLinkLabelDefault: 'Referral disclosure',
      // A broker's first email or call counts as a reply to this request
      // (SOR/2013-221 s.3(b)) only because this notice names that contact
      // before submit. Keep "one or more", "email or phone" and the request.
      privacyNoticeKey: 'preApprovalFlow.broker.consent',
      privacyNoticeDefault:
        'Ray will introduce you to one or more independent mortgage brokers. Each may contact you by email or phone about the pre-approval you asked about. Ray Rasouli does not arrange mortgages.',
    },
  ],
  analytics: {
    start: 'lead_flow_started',
    stepCompleted: 'lead_flow_step_completed',
  },
  buildTags: () => ['Buyer', 'Pre-Approval Estimate', 'Broker Referral Sent'],
  buildQualification: () => 'Estimate',
};

/** The broker capture the result's sheet renders. */
export const PRE_APPROVAL_BROKER_STEP = PRE_APPROVAL_FLOW_CONFIG.steps.find((step) => step.id === 'broker')!;
